import React from "react";
import { Building2, Check, ChevronsUpDown, Globe2 } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";

export function CompanySelector({ variant = "sidebar" }) {
    const { companies, companyId, setCompany, isSuperAdmin } = useAuth();
    const currentIsAll = isSuperAdmin() && companyId === "__all__";
    const current = companies.find((c) => c.id === companyId);
    const label = currentIsAll
        ? "Tutte le aziende"
        : current?.name || (companies[0]?.name ?? "Seleziona azienda");

    const roleLabel = current?.role
        ? current.role.charAt(0).toUpperCase() + current.role.slice(1)
        : "";

    if (companies.length === 0) {
        return (
            <div className="px-3 py-2 text-xs text-[#64748B]">
                Nessuna azienda associata
            </div>
        );
    }

    const triggerCls =
        variant === "sidebar"
            ? "w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-[#0F1D3D] hover:bg-[#182B55] border border-white/5 transition-colors text-left"
            : "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100 border border-[#DCE3EE] transition-colors";

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button data-testid="company-selector-trigger" className={triggerCls}>
                    <div className="flex items-center gap-2 min-w-0">
                        {currentIsAll ? (
                            <Globe2 className="w-4 h-4 text-[#38BDF8] shrink-0" />
                        ) : (
                            <Building2 className="w-4 h-4 text-[#38BDF8] shrink-0" />
                        )}
                        <div className="min-w-0">
                            <div className={`text-sm font-medium truncate ${variant === "sidebar" ? "text-white" : "text-[#172033]"}`}>
                                {label}
                            </div>
                            {!currentIsAll && current?.primary_pec_address && (
                                <div
                                    className={`text-[10px] font-mono truncate ${variant === "sidebar" ? "text-[#94A3B8]" : "text-[#64748B]"}`}
                                    data-testid="company-selector-primary-pec"
                                    title={current.primary_pec_address}
                                >
                                    {current.primary_pec_address}
                                </div>
                            )}
                            {roleLabel && !currentIsAll && (
                                <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">
                                    {roleLabel}
                                </div>
                            )}
                        </div>
                    </div>
                    <ChevronsUpDown className={`w-4 h-4 shrink-0 ${variant === "sidebar" ? "text-white/60" : "text-[#64748B]"}`} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                data-testid="company-selector-menu"
                align="start"
                className="w-64 bg-white border border-[#DCE3EE] shadow-lg"
            >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#64748B]">
                    Aziende
                </DropdownMenuLabel>
                {isSuperAdmin() && (
                    <>
                        <DropdownMenuItem
                            data-testid="company-selector-all"
                            onClick={() => setCompany("__all__")}
                            className="cursor-pointer"
                        >
                            <Globe2 className="w-4 h-4 mr-2 text-[#38BDF8]" />
                            <span className="flex-1">Tutte le aziende</span>
                            {currentIsAll && <Check className="w-4 h-4 text-[#16A34A]" />}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </>
                )}
                {companies.map((c) => (
                    <DropdownMenuItem
                        key={c.id}
                        data-testid={`company-selector-item-${c.id}`}
                        onClick={() => setCompany(c.id)}
                        className="cursor-pointer"
                    >
                        <Building2 className="w-4 h-4 mr-2 text-[#2563EB]" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            {c.primary_pec_address && (
                                <div className="text-[10px] font-mono text-[#64748B] truncate" title={c.primary_pec_address}>
                                    {c.primary_pec_address}
                                </div>
                            )}
                            <div className="text-[10px] text-[#64748B] uppercase tracking-wider">{c.role}</div>
                        </div>
                        {c.id === companyId && !currentIsAll && (
                            <Check className="w-4 h-4 text-[#16A34A]" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default CompanySelector;
